import * as React from 'react';
import { Field, SettingsRow, StatusPicker } from '@relay-towing/ui';
import { useDriverProfile } from '../../hooks/useDriverProfile';
import { useMember } from '../../hooks/useMember';
import { useUpdateDriverStatus } from '../../hooks/useUpdateDriverStatus';
import { DUTY_STATUSES, dutyLabel, dutyTone } from '../status';

/**
 * The duty status, set on the server (PUT /driver-mobile/status). Only a
 * driver has one, and only `drivers.edit_status` may set it; anyone else
 * gets nothing here. The old settings dropdown saved nothing and showed a
 * toast saying it had.
 */
function DutyStatusPicker({ fullWidth }: { fullWidth?: boolean }) {
  const { data: profile } = useDriverProfile();
  const updateStatus = useUpdateDriverStatus();
  const [picking, setPicking] = React.useState(false);
  return (
    <StatusPicker
      presentation="sheet"
      title="Duty status"
      prefix={null}
      fullWidth={fullWidth}
      statuses={DUTY_STATUSES}
      value={profile?.status ?? 'ON_LEAVE'}
      valueLabel={dutyLabel(profile?.status)}
      valueTone={dutyTone(profile?.status)}
      open={picking}
      onOpenChange={setPicking}
      busy={updateStatus.isPending}
      onValueChange={async (status) => {
        await updateStatus.mutateAsync(status);
        setPicking(false);
      }}
    />
  );
}

/** Whether this member sets a duty status: a driver with the key. */
export function useSetsDutyStatus(): boolean {
  const member = useMember();
  return member.drives && member.can('drivers.edit_status');
}

/** The duty status as a row of Settings, among the other rows. */
export function DutyStatusRow() {
  if (!useSetsDutyStatus()) return null;
  return <SettingsRow label="Duty status" control={<DutyStatusPicker />} />;
}

/**
 * The duty status on its own, its label over the picker the width of the
 * sheet: the presence menu's, where a card round one row read as clutter.
 */
export function DutyStatusField() {
  if (!useSetsDutyStatus()) return null;
  return (
    <Field label="Duty status">
      <DutyStatusPicker fullWidth />
    </Field>
  );
}
